import React, { useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import MonthlyReview from "./MonthlyReview";

import { useKpiDashboard } from "../../hooks/useKpiDashboard";
import { getAvailablePeriods } from "./kpiDataHelpers";
import type { SelectedPeriod } from "../../types/kpiDashboard";

import WeeklyDashboard from "./components/WeeklyDashboard";
import EnergyDetailDialog from "./components/EnergyDetailDialog";
import StationsDetailDialog from "./components/StationsDetailDialog";
import OnlineRateDetailDialog from "./components/OnlineRateDetailDialog";
import SessionsDetailDialog from "./components/SessionsDetailDialog";
import SyncKpiDataDialog from "./components/SyncKpiDataDialog";

import "./KpiDashboard.css";

const AVAILABLE_PERIODS = getAvailablePeriods();

const KpiDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriod>(
    () => AVAILABLE_PERIODS[0] ?? { year: 0, week: 0 }
  );
  const [energyDialogVisible, setEnergyDialogVisible] = useState(false);
  const [stationsDialogVisible, setStationsDialogVisible] = useState(false);
  const [onlineRateDialogVisible, setOnlineRateDialogVisible] = useState(false);
  const [sessionsDialogVisible, setSessionsDialogVisible] = useState(false);
  const [syncDialogVisible, setSyncDialogVisible] = useState(false);

  const { data, isLoading, error, fetchKpiData } = useKpiDashboard(selectedPeriod);

  if (AVAILABLE_PERIODS.length === 0) {
    return (
      <div className="kpi-dashboard">
        <SyncKpiDataDialog
          visible={syncDialogVisible}
          onHide={() => setSyncDialogVisible(false)}
        />
        <div
          className="flex flex-column align-items-center justify-content-center"
          style={{ minHeight: 400 }}
        >
          <i className="pi pi-database text-5xl text-color-secondary mb-4" />
          <p className="text-xl text-color-secondary mb-4">
            No data available. Please sync from Google Sheets.
          </p>
          <Button
            label="Sync from Sheets"
            icon="pi pi-sync"
            onClick={() => setSyncDialogVisible(true)}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="kpi-loading">
        <ProgressSpinner style={{ width: 52 }} />
        <p className="text-color-secondary mt-3">Loading KPI data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-error">
        <Message severity="error" text={error} />
        <Button label="Retry" icon="pi pi-refresh" className="mt-3" onClick={fetchKpiData} />
      </div>
    );
  }

  return (
    <div className="kpi-dashboard">
      <EnergyDetailDialog
        visible={energyDialogVisible}
        onHide={() => setEnergyDialogVisible(false)}
        trend={data.weeklyTrend}
      />
      <StationsDetailDialog
        visible={stationsDialogVisible}
        onHide={() => setStationsDialogVisible(false)}
        trend={data.weeklyTrend}
      />
      <OnlineRateDetailDialog
        visible={onlineRateDialogVisible}
        onHide={() => setOnlineRateDialogVisible(false)}
        trend={data.weeklyTrend}
      />
      <SessionsDetailDialog
        visible={sessionsDialogVisible}
        onHide={() => setSessionsDialogVisible(false)}
        trend={data.weeklyTrend}
      />
      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header="Weekly">
          <WeeklyDashboard
            data={data}
            selectedPeriod={selectedPeriod}
            availablePeriods={AVAILABLE_PERIODS}
            onPeriodChange={setSelectedPeriod}
            onEnergyClick={() => setEnergyDialogVisible(true)}
            onStationsClick={() => setStationsDialogVisible(true)}
            onOnlineRateClick={() => setOnlineRateDialogVisible(true)}
            onSessionsClick={() => setSessionsDialogVisible(true)}
          />
        </TabPanel>
        <TabPanel header="Monthly"><MonthlyReview /></TabPanel>
      </TabView>
    </div>
  );
};

export default KpiDashboard;
